import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Search, Receipt, Calendar, ChevronRight,
  TrendingUp, TrendingDown, ArrowRightLeft
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('sv-SE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
};

export default function MobileBookkeeping() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, totalDebit: 0 });

  const token = localStorage.getItem('token');

  const fetchEntries = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/journal-entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const entryList = Array.isArray(data) ? data : [];
      setEntries(entryList);
      
      // Calculate stats
      const now = new Date();
      const thisMonth = entryList.filter(e => {
        const entryDate = new Date(e.date);
        return entryDate.getMonth() === now.getMonth() && entryDate.getFullYear() === now.getFullYear();
      });
      
      setStats({
        total: entryList.length,
        thisMonth: thisMonth.length,
        totalDebit: entryList.reduce((sum, e) => sum + (e.total_debit || 0), 0)
      });
    } catch (err) {
      console.error('Error fetching entries:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const filteredEntries = entries.filter(entry => 
    !searchQuery || 
    entry.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    entry.entry_number?.toString().includes(searchQuery) ||
    entry.document_ref?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('bookkeeping.title')}</div>
        </div>
        <button className="mobile-header-btn" onClick={() => navigate('/m/bookkeeping/new')}>
          <Plus size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="mobile-content">
        {/* Stats */}
        <div className="mobile-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="mobile-stat-card">
            <div className="mobile-stat-icon purple" style={{ width: '40px', height: '40px' }}>
              <Receipt size={20} />
            </div>
            <div className="mobile-stat-value" style={{ fontSize: '20px' }}>{stats.total}</div>
            <div className="mobile-stat-label">Verifikationer</div>
          </div>
          <div className="mobile-stat-card">
            <div className="mobile-stat-icon blue" style={{ width: '40px', height: '40px' }}>
              <Calendar size={20} />
            </div>
            <div className="mobile-stat-value" style={{ fontSize: '20px' }}>{stats.thisMonth}</div>
            <div className="mobile-stat-label">Denna månad</div>
          </div>
          <div className="mobile-stat-card">
            <div className="mobile-stat-icon green" style={{ width: '40px', height: '40px' }}>
              <TrendingUp size={20} />
            </div>
            <div className="mobile-stat-value" style={{ fontSize: '18px' }}>{formatCurrency(stats.totalDebit)}</div>
            <div className="mobile-stat-label">Total debet</div>
          </div>
        </div>

        {/* Search */}
        <div className="mobile-search">
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder="Sök verifikationer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Entry List */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="mobile-empty">
            <div className="mobile-empty-icon"><Receipt size={48} /></div>
            <div className="mobile-empty-title">{t('common.noData')}</div>
            <div className="mobile-empty-desc">Skapa din första verifikation</div>
            <button 
              className="mobile-btn mobile-btn-primary" 
              style={{ marginTop: '16px', width: 'auto', padding: '12px 24px' }}
              onClick={() => navigate('/m/bookkeeping/new')}
            >
              <Plus size={18} />
              {t('bookkeeping.newEntry')}
            </button>
          </div>
        ) : (
          <div className="mobile-list">
            {filteredEntries.map((entry, idx) => (
              <div 
                key={idx} 
                className="mobile-list-item"
                onClick={() => navigate(`/m/bookkeeping/${entry.id}`)}
              >
                <div className="mobile-list-icon" style={{ background: '#f3e8ff', color: '#9333ea' }}>
                  <Receipt size={20} />
                </div>
                <div className="mobile-list-content">
                  <div className="mobile-list-title">
                    #{entry.entry_number} - {entry.description || 'Verifikation'}
                  </div>
                  <div className="mobile-list-subtitle">
                    {formatDate(entry.date)}
                    {entry.document_ref && ` • ${entry.document_ref}`}
                  </div>
                </div>
                <div className="mobile-list-value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '13px', color: '#16a34a', fontWeight: 500 }}>
                        D: {formatCurrency(entry.total_debit)}
                      </div>
                      <div style={{ fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
                        K: {formatCurrency(entry.total_credit)}
                      </div>
                    </div>
                    <ChevronRight size={18} color="#94a3b8" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* FAB */}
      <button className="mobile-fab" onClick={() => navigate('/m/bookkeeping/new')}>
        <Plus size={24} />
      </button>
    </div>
  );
}
